import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { getApiUrl, isBackendConfigured } from '../config';

const DriverAuthContext = createContext();

export const useDriverAuth = () => {
  const context = useContext(DriverAuthContext);
  if (!context) {
    throw new Error('useDriverAuth must be used within DriverAuthProvider');
  }
  return context;
};

export const DriverAuthProvider = ({ children }) => {
  const [driver, setDriver] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('driver_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setDriver(null);
    localStorage.removeItem('driver_token');
  }, []);

  const fetchDriver = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/api/drivers/me'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDriver(response.data);
    } catch (error) {
      console.error('Error fetching driver:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      fetchDriver();
    } else {
      setLoading(false);
    }
  }, [token, fetchDriver]);

  const signup = async (signupData) => {
    try {
      const response = await axios.post(getApiUrl('/api/drivers/signup'), signupData);
      setToken(response.data.token);
      setDriver(response.data.driver);
      localStorage.setItem('driver_token', response.data.token);
      return { success: true, driver: response.data.driver };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Signup failed',
      };
    }
  };

  const login = async (loginData) => {
    try {
      const response = await axios.post(getApiUrl('/api/drivers/login'), loginData);
      setToken(response.data.token);
      setDriver(response.data.driver);
      localStorage.setItem('driver_token', response.data.token);
      return { success: true, driver: response.data.driver };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const signAgreement = async (legalName) => {
    try {
      const response = await axios.post(
        getApiUrl('/api/drivers/agreement'),
        { legalName, agreed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDriver(response.data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to sign agreement',
      };
    }
  };

  const refreshDriver = fetchDriver;

  const hasSignedAgreement = Boolean(driver?.agreementSignedAt);

  return (
    <DriverAuthContext.Provider
      value={{
        driver,
        token,
        loading,
        isAuthenticated: !!driver,
        hasSignedAgreement,
        signup,
        login,
        logout,
        signAgreement,
        refreshDriver,
      }}
    >
      {children}
    </DriverAuthContext.Provider>
  );
};
